import { firestore } from '../firebase/config';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';

// Collection reference
const transactionsCollection = collection(firestore, 'transactions');

/**
 * Get all transactions with optional filters
 * @param {Object} filters - Optional filters (status, type, userId, dateRange)
 * @returns {Promise<Array>} Array of transactions
 */
export const getAllTransactions = async (filters = {}) => {
  try {
    let q = query(transactionsCollection);
    
    // Apply filters
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    if (filters.type) {
      q = query(q, where('type', '==', filters.type));
    }
    
    if (filters.userId) {
      q = query(q, where('userId', '==', filters.userId));
    }
    
    if (filters.bookingId) {
      q = query(q, where('bookingId', '==', filters.bookingId));
    }
    
    if (filters.startDate && filters.endDate) {
      q = query(
        q,
        where('createdAt', '>=', Timestamp.fromDate(new Date(filters.startDate))),
        where('createdAt', '<=', Timestamp.fromDate(new Date(filters.endDate)))
      );
    }
    
    // Order by creation date (newest first)
    q = query(q, orderBy('createdAt', 'desc'));
    
    // Apply limit if specified
    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
      processedAt: doc.data().processedAt?.toDate?.() || doc.data().processedAt
    }));
  } catch (error) {
    console.error('Error fetching transactions:', error);
    throw new Error('Erreur lors de la récupération des transactions');
  }
};

/**
 * Get a single transaction by ID
 * @param {string} transactionId - Transaction ID
 * @returns {Promise<Object>} Transaction data
 */
export const getTransactionById = async (transactionId) => {
  try {
    const transactionDoc = await getDoc(doc(firestore, 'transactions', transactionId));
    
    if (!transactionDoc.exists()) {
      throw new Error('Transaction non trouvée');
    }
    
    const data = transactionDoc.data();
    return {
      id: transactionDoc.id,
      ...data,
      createdAt: data.createdAt?.toDate?.() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
      processedAt: data.processedAt?.toDate?.() || data.processedAt
    };
  } catch (error) {
    console.error('Error fetching transaction:', error);
    throw new Error('Erreur lors de la récupération de la transaction');
  }
};

/**
 * Get transactions by user ID
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of transactions
 */
export const getTransactionsByUser = async (userId) => {
  try {
    const q = query(
      transactionsCollection,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
      processedAt: doc.data().processedAt?.toDate?.() || doc.data().processedAt
    }));
  } catch (error) {
    console.error('Error fetching user transactions:', error);
    throw new Error('Erreur lors de la récupération des transactions de l\'utilisateur');
  }
};

/**
 * Get transactions by booking ID
 * @param {string} bookingId - Booking ID
 * @returns {Promise<Array>} Array of transactions
 */
export const getTransactionsByBooking = async (bookingId) => {
  try {
    const q = query(
      transactionsCollection,
      where('bookingId', '==', bookingId),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
      processedAt: doc.data().processedAt?.toDate?.() || doc.data().processedAt
    }));
  } catch (error) {
    console.error('Error fetching booking transactions:', error);
    throw new Error('Erreur lors de la récupération des transactions de la réservation');
  }
};

/**
 * Create a new transaction
 * @param {Object} transactionData - Transaction data
 * @returns {Promise<Object>} Created transaction with ID
 */
export const createTransaction = async (transactionData) => {
  try {
    const now = new Date();
    
    const newTransaction = {
      ...transactionData,
      status: transactionData.status || 'Pending',
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now)
    };
    
    const docRef = await addDoc(transactionsCollection, newTransaction);
    
    return {
      id: docRef.id,
      ...newTransaction,
      createdAt: now,
      updatedAt: now
    };
  } catch (error) {
    console.error('Error creating transaction:', error);
    throw new Error('Erreur lors de la création de la transaction');
  }
};

/**
 * Update an existing transaction
 * @param {string} transactionId - Transaction ID
 * @param {Object} transactionData - Updated transaction data
 * @returns {Promise<Object>} Updated transaction
 */
export const updateTransaction = async (transactionId, transactionData) => {
  try {
    const transactionRef = doc(firestore, 'transactions', transactionId);
    
    const updateData = {
      ...transactionData,
      updatedAt: Timestamp.fromDate(new Date())
    };
    
    // If status is being changed to 'Completed' or 'Failed', add processedAt timestamp
    if ((transactionData.status === 'Completed' || transactionData.status === 'Failed') && !transactionData.processedAt) {
      updateData.processedAt = Timestamp.fromDate(new Date());
    }
    
    await updateDoc(transactionRef, updateData);
    
    // Fetch and return updated document
    return await getTransactionById(transactionId);
  } catch (error) {
    console.error('Error updating transaction:', error);
    throw new Error('Erreur lors de la mise à jour de la transaction');
  }
};

/**
 * Delete a transaction
 * @param {string} transactionId - Transaction ID
 * @returns {Promise<void>}
 */
export const deleteTransaction = async (transactionId) => {
  try {
    await deleteDoc(doc(firestore, 'transactions', transactionId));
  } catch (error) {
    console.error('Error deleting transaction:', error);
    throw new Error('Erreur lors de la suppression de la transaction');
  }
};

/**
 * Get transaction statistics
 * @returns {Promise<Object>} Transaction statistics
 */
export const getTransactionStats = async () => {
  try {
    const snapshot = await getDocs(transactionsCollection);
    const transactions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    const stats = {
      total: transactions.length,
      totalAmount: 0,
      pending: 0,
      pendingAmount: 0,
      completed: 0,
      completedAmount: 0,
      failed: 0,
      failedAmount: 0,
      refunded: 0,
      refundedAmount: 0,
      byType: {
        Payment: 0,
        Refund: 0,
        Commission: 0,
        Other: 0
      }
    };
    
    transactions.forEach(transaction => {
      stats.totalAmount += transaction.amount || 0;
      
      // Count by status
      switch (transaction.status) {
        case 'Pending':
          stats.pending++;
          stats.pendingAmount += transaction.amount || 0;
          break;
        case 'Completed':
          stats.completed++;
          stats.completedAmount += transaction.amount || 0;
          break;
        case 'Failed':
          stats.failed++;
          stats.failedAmount += transaction.amount || 0;
          break;
        case 'Refunded':
          stats.refunded++;
          stats.refundedAmount += transaction.amount || 0;
          break;
        default:
          break;
      }
      
      // Count by type
      if (stats.byType.hasOwnProperty(transaction.type)) {
        stats.byType[transaction.type]++;
      } else {
        stats.byType.Other++;
      }
    });
    
    return stats;
  } catch (error) {
    console.error('Error fetching transaction stats:', error);
    throw new Error('Erreur lors de la récupération des statistiques');
  }
};

/**
 * Process a pending transaction
 * @param {string} transactionId - Transaction ID
 * @param {boolean} success - Whether the transaction was successful
 * @param {Object} details - Additional processing details
 * @returns {Promise<Object>} Updated transaction
 */
export const processTransaction = async (transactionId, success = true, details = {}) => {
  try {
    const transactionRef = doc(firestore, 'transactions', transactionId);
    
    const updateData = {
      status: success ? 'Completed' : 'Failed',
      processedAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
      ...details
    };
    
    await updateDoc(transactionRef, updateData);
    
    return await getTransactionById(transactionId);
  } catch (error) {
    console.error('Error processing transaction:', error);
    throw new Error('Erreur lors du traitement de la transaction');
  }
};

/**
 * Refund a transaction
 * @param {string} transactionId - Transaction ID
 * @param {number} refundAmount - Amount to refund (optional, defaults to full amount)
 * @param {string} reason - Refund reason
 * @returns {Promise<Object>} Updated transaction
 */
export const refundTransaction = async (transactionId, refundAmount = null, reason = '') => {
  try {
    const transaction = await getTransactionById(transactionId);
    
    if (transaction.status !== 'Completed') {
      throw new Error('Seules les transactions complétées peuvent être remboursées');
    }
    
    const transactionRef = doc(firestore, 'transactions', transactionId);
    
    const updateData = {
      status: 'Refunded',
      refundAmount: refundAmount || transaction.amount,
      refundReason: reason,
      refundedAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date())
    };
    
    await updateDoc(transactionRef, updateData);
    
    return await getTransactionById(transactionId);
  } catch (error) {
    console.error('Error refunding transaction:', error);
    throw new Error('Erreur lors du remboursement de la transaction');
  }
};

export default {
  getAllTransactions,
  getTransactionById,
  getTransactionsByUser,
  getTransactionsByBooking,
  createTransaction,
  updateTransaction,
  deleteTransaction,
  getTransactionStats,
  processTransaction,
  refundTransaction
};
